import React, { useState } from 'react';
import { Play, Clock, Calendar, ChevronDown } from 'lucide-react';

const EpisodesList = ({ currentMovie, allMovies, onSelectEpisode }) => {
    const [hoveredId, setHoveredId] = useState(null);
    const [collapsedSeasons, setCollapsedSeasons] = useState({});

    if (currentMovie.type !== 'serie') return null;

    const seriesEpisodes = allMovies.filter(m =>
        m.type === 'serie' &&
        m.tmdbId === currentMovie.tmdbId &&
        m.episode
    );

    if (seriesEpisodes.length === 0) return null;

    const episodesBySeason = seriesEpisodes.reduce((acc, ep) => {
        const s = ep.season || 1;
        if (!acc[s]) acc[s] = [];
        acc[s].push(ep);
        return acc;
    }, {});

    Object.keys(episodesBySeason).forEach(s => {
        episodesBySeason[s].sort((a, b) => a.episode - b.episode);
    });

    const seasons = Object.keys(episodesBySeason).sort((a, b) => Number(a) - Number(b));

    const toggleSeason = (s) => {
        setCollapsedSeasons(prev => ({ ...prev, [s]: !prev[s] }));
    };

    return (
        <div
            style={{
                marginTop: 32,
                borderTop: '1px solid var(--glass-border)',
                paddingTop: 32,
            }}
        >
            <h3
                className="section-title"
                style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}
            >
                <Play style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                Épisodes disponibles
                {currentMovie.numberOfSeasons && (
                    <span style={{ color: 'var(--text-4)', fontSize: '0.875rem', fontWeight: 400 }}>
                        {currentMovie.numberOfSeasons} saison{currentMovie.numberOfSeasons > 1 ? 's' : ''}
                    </span>
                )}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {seasons.map(seasonNumber => {
                    const isCollapsed = collapsedSeasons[seasonNumber];

                    return (
                        <div
                            key={seasonNumber}
                            style={{
                                borderRadius: 'var(--r-lg)',
                                background: 'var(--glass)',
                                border: '1px solid var(--glass-border)',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Season header */}
                            <button
                                onClick={() => toggleSeason(seasonNumber)}
                                style={{
                                    width: '100%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '14px 16px',
                                    background: 'transparent', border: 'none',
                                    cursor: 'pointer', textAlign: 'left',
                                    transition: 'background 150ms ease',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--glass-hover)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span
                                        style={{
                                            display: 'inline-flex', alignItems: 'center',
                                            padding: '2px 10px', borderRadius: 'var(--r-full)',
                                            background: 'rgba(59,130,246,0.2)',
                                            border: '1px solid rgba(59,130,246,0.3)',
                                            color: '#93C5FD', fontSize: '0.75rem', fontWeight: 700,
                                        }}
                                    >
                                        S{seasonNumber}
                                    </span>
                                    <span style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '0.9375rem' }}>
                                        Saison {seasonNumber}
                                    </span>
                                    <span style={{ color: 'var(--text-4)', fontSize: '0.8125rem' }}>
                                        {episodesBySeason[seasonNumber].length} épisode{episodesBySeason[seasonNumber].length > 1 ? 's' : ''}
                                    </span>
                                </div>
                                <ChevronDown
                                    style={{
                                        width: 18, height: 18, color: 'var(--text-4)',
                                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0)',
                                        transition: 'transform 200ms ease',
                                        flexShrink: 0,
                                    }}
                                />
                            </button>

                            {/* Episodes list */}
                            {!isCollapsed && (
                                <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {episodesBySeason[seasonNumber].map(episode => {
                                        const isCurrent = episode.id === currentMovie.id;

                                        return (
                                            <div
                                                key={episode.id}
                                                onClick={() => !isCurrent && onSelectEpisode(episode)}
                                                onMouseEnter={() => !isCurrent && setHoveredId(episode.id)}
                                                onMouseLeave={() => setHoveredId(null)}
                                                style={{
                                                    display: 'flex',
                                                    gap: 12,
                                                    padding: 10,
                                                    borderRadius: 'var(--r-md)',
                                                    background: isCurrent
                                                        ? 'linear-gradient(135deg, rgba(193,145,60,0.2), rgba(232,183,90,0.12))'
                                                        : hoveredId === episode.id
                                                            ? 'var(--glass-hover)'
                                                            : 'transparent',
                                                    border: `1px solid ${isCurrent
                                                        ? 'rgba(232,183,90,0.3)'
                                                        : hoveredId === episode.id
                                                            ? 'var(--glass-border-hover)'
                                                            : 'transparent'}`,
                                                    cursor: isCurrent ? 'default' : 'pointer',
                                                    transition: 'all 150ms ease',
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                {/* Thumbnail */}
                                                <div
                                                    style={{
                                                        position: 'relative', flexShrink: 0,
                                                        width: 'min(140px, 38vw)',
                                                        borderRadius: 'var(--r-sm)',
                                                        overflow: 'hidden',
                                                    }}
                                                    className="hidden sm:block"
                                                >
                                                    <img
                                                        src={episode.episodeStill || episode.backdrop || episode.poster}
                                                        alt={episode.episodeTitle}
                                                        style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                                                    />
                                                    {isCurrent ? (
                                                        <div
                                                            style={{
                                                                position: 'absolute', inset: 0,
                                                                background: 'rgba(232,183,90,0.5)',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    padding: '3px 10px', borderRadius: 'var(--r-full)',
                                                                    background: 'rgba(255,255,255,0.9)',
                                                                    color: 'var(--accent-deep)', fontSize: 10, fontWeight: 700,
                                                                    letterSpacing: '0.06em', textTransform: 'uppercase',
                                                                }}
                                                            >
                                                                En cours
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            style={{
                                                                position: 'absolute', inset: 0,
                                                                background: 'rgba(0,0,0,0.5)',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                opacity: hoveredId === episode.id ? 1 : 0,
                                                                transition: 'opacity 200ms ease',
                                                            }}
                                                        >
                                                            <Play style={{ width: 22, height: 22, color: '#fff', fill: '#fff' }} />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Info */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                                                        <h5 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.3 }}>
                                                            <span style={{ color: isCurrent ? 'var(--accent-bright)' : 'var(--text-4)', marginRight: 5, fontWeight: 600 }}>
                                                                {episode.episode}.
                                                            </span>
                                                            {episode.episodeTitle || `Épisode ${episode.episode}`}
                                                        </h5>
                                                        {episode.duration && (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--text-4)', fontSize: '0.75rem', flexShrink: 0 }}>
                                                                <Clock style={{ width: 11, height: 11 }} />
                                                                {episode.duration}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <p
                                                        style={{
                                                            color: 'var(--text-3)',
                                                            fontSize: '0.8125rem',
                                                            lineHeight: 1.55,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            marginBottom: episode.episodeAirDate ? 6 : 0,
                                                        }}
                                                    >
                                                        {episode.episodeOverview || episode.overview}
                                                    </p>

                                                    {episode.episodeAirDate && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-4)', fontSize: '0.6875rem' }}>
                                                            <Calendar style={{ width: 10, height: 10 }} />
                                                            <span className="hidden sm:inline">
                                                                {new Date(episode.episodeAirDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                            </span>
                                                            <span className="sm:hidden">
                                                                {new Date(episode.episodeAirDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {seriesEpisodes.length === 1 && (
                <div
                    style={{
                        marginTop: 16, padding: '12px 16px',
                        borderRadius: 'var(--r-md)',
                        background: 'rgba(245,158,11,0.08)',
                        border: '1px solid rgba(245,158,11,0.2)',
                        color: '#FCD34D', fontSize: '0.8125rem',
                    }}
                >
                    💡 Seul épisode disponible pour le moment.
                </div>
            )}
        </div>
    );
};

export default EpisodesList;
